import { createLogger } from "../helpers/logger.js";

function tryTCF(timeout = 8000) {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    let tcfapi = null;

    try {
      if (window.top && window.top !== window && typeof window.top.__tcfapi === "function") {
        tcfapi = window.top.__tcfapi;
      }
    } catch (e) {
      // cross-origin top; can't access
    }

    if (!tcfapi && typeof window.__tcfapi === "function") {
      tcfapi = window.__tcfapi;
    }

    if (!tcfapi) {
      return resolve({
        ok: false,
        status: "unavailable",
        allowed: true,
        personalized: false,
        source: "tcf",
        gdprApplies: null,
        tcString: null,
        vendorConsents: {},
        elapsedMs: Date.now() - startedAt,
      });
    }

    let done = false;

    const timer = setTimeout(() => {
      if (done) return;
      done = true;

      resolve({
        ok: false,
        status: "timeout",
        allowed: true,
        personalized: false,
        source: "tcf",
        gdprApplies: null,
        tcString: null,
        vendorConsents: {},
        elapsedMs: Date.now() - startedAt,
      });
    }, timeout);

    try {
      tcfapi("getTCData", 2, (tcData, success) => {
        if (done) return;
        done = true;
        clearTimeout(timer);

        if (!success || !tcData) {
          return resolve({
            ok: false,
            status: "error",
            allowed: true,
            personalized: false,
            source: "tcf",
            gdprApplies: null,
            tcString: null,
            vendorConsents: {},
            elapsedMs: Date.now() - startedAt,
          });
        }

        if (tcData.gdprApplies === false) {
          return resolve({
            ok: true,
            status: "ready",
            allowed: true,
            personalized: false,
            source: "tcf",
            gdprApplies: false,
            tcString: null,
            vendorConsents: {},
            elapsedMs: Date.now() - startedAt,
          });
        }

        const p = tcData.purpose?.consents || {};

        return resolve({
          ok: true,
          status: "ready",
          allowed: p["2"] === true,
          personalized: p["3"] === true,
          source: "tcf",
          gdprApplies: tcData.gdprApplies,
          tcString: tcData.tcString || null,
          vendorConsents: tcData.vendor?.consents || {},
          elapsedMs: Date.now() - startedAt,
        });
      });
    } catch (e) {
      if (done) return;
      done = true;
      clearTimeout(timer);

      resolve({
        ok: false,
        status: "error",
        allowed: true,
        personalized: false,
        source: "tcf",
        gdprApplies: null,
        tcString: null,
        vendorConsents: {},
        error: e?.message || String(e),
        elapsedMs: Date.now() - startedAt,
      });
    }
  });
}

function tryGPP(timeout = 8000) {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    let gpp = null;

    try {
      if (window.top && window.top !== window && typeof window.top.__gpp === "function") {
        gpp = window.top.__gpp;
      }
    } catch (e) {}

    if (!gpp && typeof window.__gpp === "function") {
      gpp = window.__gpp;
    }

    if (!gpp) {
      return resolve({
        ok: false,
        status: "unavailable",
        allowed: true,
        personalized: false,
        source: "gpp",
        gppString: null,
        gppSid: null,
        elapsedMs: Date.now() - startedAt,
      });
    }

    let done = false;

    const timer = setTimeout(() => {
      if (done) return;
      done = true;

      resolve({
        ok: false,
        status: "timeout",
        allowed: true,
        personalized: false,
        source: "gpp",
        gppString: null,
        gppSid: null,
        elapsedMs: Date.now() - startedAt,
      });
    }, timeout);

    try {
      gpp("ping", (data, success) => {
        if (done) return;
        done = true;
        clearTimeout(timer);

        if (!success || !data) {
          return resolve({
            ok: false,
            status: "error",
            allowed: true,
            personalized: false,
            source: "gpp",
            gppString: null,
            gppSid: null,
            elapsedMs: Date.now() - startedAt,
          });
        }

        const gppString = data.gppString || null;

        const applicable = Array.isArray(data.applicableSections)
          ? data.applicableSections
          : Array.isArray(data.sectionList)
            ? data.sectionList
            : [];

        const gppSid = applicable.length ? applicable.join(",") : null;
        const parsed = data.parsedSections || null;
        const personalized = parsed ? gppAllowsPersonalizedFromParsed(parsed) : false;

        resolve({
          ok: true,
          status: "ready",
          allowed: true,
          personalized,
          source: "gpp",
          gppString,
          gppSid,
          elapsedMs: Date.now() - startedAt,
        });
      });
    } catch (e) {
      if (done) return;
      done = true;
      clearTimeout(timer);

      resolve({
        ok: false,
        status: "error",
        allowed: true,
        personalized: false,
        source: "gpp",
        gppString: null,
        gppSid: null,
        error: e?.message || String(e),
        elapsedMs: Date.now() - startedAt,
      });
    }
  });
}

// Conservative parser across MSPA-style sections.
// If ANY section says user opted out of sale/share/targeted advertising -> not personalized.
function gppAllowsPersonalizedFromParsed(parsedSections) {
  const isOptOut = (v) => {
    if (v === true) return true;
    if (v === false || v == null) return false;
    const s = String(v).trim().toUpperCase();
    return s === "1" || s === "Y" || s === "YES" || s === "TRUE";
  };

  const hasAnyOptOut = (sectionObj) => {
    if (!sectionObj || typeof sectionObj !== "object") return false;

    const fields = [
      "SaleOptOut",
      "SharingOptOut",
      "TargetedAdvertisingOptOut",
    ];

    for (const key of fields) {
      if (key in sectionObj && isOptOut(sectionObj[key])) return true;

      const lower = key.toLowerCase();
      for (const k of Object.keys(sectionObj)) {
        if (k.toLowerCase() === lower && isOptOut(sectionObj[k])) return true;
      }
    }

    return false;
  };

  const sections = Array.isArray(parsedSections)
    ? parsedSections
    : typeof parsedSections === "object"
      ? Object.values(parsedSections)
      : [];

  for (const sec of sections) {
    if (hasAnyOptOut(sec)) return false;
  }

  return true;
}

function tryUSP(timeout = 8000) {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    let uspapi = null;

    try {
      if (window.top && window.top !== window && typeof window.top.__uspapi === "function") {
        uspapi = window.top.__uspapi;
      }
    } catch (e) {}

    if (!uspapi && typeof window.__uspapi === "function") {
      uspapi = window.__uspapi;
    }

    if (!uspapi) {
      return resolve({
        ok: false,
        status: "unavailable",
        allowed: true,
        personalized: false,
        source: "usp",
        uspString: null,
        elapsedMs: Date.now() - startedAt,
      });
    }

    let done = false;

    const timer = setTimeout(() => {
      if (done) return;
      done = true;

      resolve({
        ok: false,
        status: "timeout",
        allowed: true,
        personalized: false,
        source: "usp",
        uspString: null,
        elapsedMs: Date.now() - startedAt,
      });
    }, timeout);

    try {
      uspapi("getUSPData", 1, (uspData, success) => {
        if (done) return;
        done = true;
        clearTimeout(timer);

        if (!success || !uspData) {
          return resolve({
            ok: false,
            status: "error",
            allowed: true,
            personalized: false,
            source: "usp",
            uspString: null,
            elapsedMs: Date.now() - startedAt,
          });
        }

        const optOut = uspData.uspString?.[2] === "Y";

        resolve({
          ok: true,
          status: "ready",
          allowed: !optOut,
          personalized: !optOut,
          source: "usp",
          uspString: uspData.uspString || null,
          elapsedMs: Date.now() - startedAt,
        });
      });
    } catch (e) {
      if (done) return;
      done = true;
      clearTimeout(timer);

      resolve({
        ok: false,
        status: "error",
        allowed: true,
        personalized: false,
        source: "usp",
        uspString: null,
        error: e?.message || String(e),
        elapsedMs: Date.now() - startedAt,
      });
    }
  });
}

export async function getUniversalConsent(timeout = 8000, debug = false) {
  const tcfPromise = tryTCF(timeout);
  const gppPromise = tryGPP(Math.min(timeout, 4000));
  const uspPromise = tryUSP(Math.min(timeout, 4000));

  const log = createLogger("ConsentBridge", debug);

  const [tcf, gpp, usp] = await Promise.all([
    tcfPromise,
    gppPromise,
    uspPromise
  ]);

  

  const out = {
    allowed: true,
    personalized: false,
    source: [],
    gdprApplies: null,
    tcString: null,
    gppString: null,
    gppSid: null,
    uspString: null,
    vendorConsents: {},
    consentStatus: {
      tcf: tcf?.status || "unknown",
      gpp: gpp?.status || "unknown",
      usp: usp?.status || "unknown",
    },
    consentElapsedMs: {
      tcf: tcf?.elapsedMs ?? null,
      gpp: gpp?.elapsedMs ?? null,
      usp: usp?.elapsedMs ?? null,
    },
  };

  if (tcf?.ok) {
    out.source.push("tcf");
    out.personalized = !!tcf.personalized;
    out.gdprApplies = typeof tcf.gdprApplies === "boolean" ? tcf.gdprApplies : null;
    out.tcString = tcf.tcString || null;
    out.vendorConsents = tcf.vendorConsents || {};
  } else if (tcf?.status && tcf.status !== "unavailable") {
    out.source.push(`tcf:${tcf.status}`);
  }

  if (gpp?.ok) {
    out.source.push("gpp");
    out.gppString = gpp.gppString || null;
    out.gppSid = gpp.gppSid || null;
    if (gpp.personalized === false) out.personalized = false;
  } else if (gpp?.status && gpp.status !== "unavailable") {
    out.source.push(`gpp:${gpp.status}`);
  }

  if (usp?.ok) {
    out.source.push("usp");
    out.uspString = usp.uspString || null;
    if (usp.personalized === false) out.personalized = false;
  } else if (usp?.status && usp.status !== "unavailable") {
    out.source.push(`usp:${usp.status}`);
  }

  if (!out.source.length) out.source = ["none"];

  if (tcf?.status === "timeout" || gpp?.status === "timeout" || usp?.status === "timeout") {
    log.debug("⚠ Consent API timeout", {
      source: out.source,
      consentStatus: out.consentStatus,
      consentElapsedMs: out.consentElapsedMs,
      gdprApplies: out.gdprApplies,
      hasTcString: !!out.tcString,
      hasGppString: !!out.gppString,
      gppSid: out.gppSid,
    });
  }

  if (
    out.gdprApplies !== false &&
    !out.tcString &&
    ["timeout", "error", "unavailable"].includes(tcf?.status)
  ) {
    log.debug("⚠ Continuing without TCF consent string", {
      tcfStatus: tcf?.status,
      gdprApplies: out.gdprApplies,
      hasTcString: !!out.tcString,
      tcfElapsedMs: tcf?.elapsedMs,
    });
  }

  log.debug("Universal consent resolved", {
    source: out.source,
    consentStatus: out.consentStatus,
    consentElapsedMs: out.consentElapsedMs,
    gdprApplies: out.gdprApplies,
    hasTcString: !!out.tcString,
    tcStringLength: out.tcString?.length || 0,
    hasGppString: !!out.gppString,
    gppSid: out.gppSid,
    hasUspString: !!out.uspString,
    personalized: out.personalized,
  });

  return out;
}
